/**
 * Orders
 */

// React
import React, { useEffect, useState, useContext, FC } from "react";

// Context
import AuthContext, { AuthConsumer } from "../../authContext";

// UI
import theme from "../../theme";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import {
  Typography,
  Grid,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Card,
  CardHeader,
  CardContent,
  Divider,
} from "@material-ui/core";

// Icon
import Icon from "@mdi/react";
import {
  mdiAccountOutline,
  mdiPhoneOutline,
  mdiHomeSearchOutline,
  mdiPackageVariant,
  mdiStateMachine,
  mdiCalendar,
  mdiClose,
  mdiHomeCityOutline,
  mdiPencilOutline,
  mdiAlphabeticalVariant,
} from "@mdi/js";
import {
  CreatedIcon,
  AssignedIcon,
  PickIcon,
  PackIcon,
  SupplyingIcon,
  VipIcon,
  VvipIcon,
} from "./iconCollection";
// Components
import RenderFilter from "../../utils/listDaynamicFilterHookForm";
import type { Filter } from "../../utils/listDaynamicFilter";
import List from "./warehouseAdminList";
import Pagination from "@material-ui/lab/Pagination";
import Can from "../../components/Can";
import CSV from "./csv";
import ExelExport from "./components/exelExport";
// Helper
import {
  removeEmptyValueObject,
  translate,
  checkMatchMedia,
} from "../../utils/helpers";
import useQuery from "../../utils/hook/useQuery";

// Type
import { Props, Status } from "./interfaces";

import handleError from "../../utils/handleError";
import handleNotfication from "../../utils/handleNotification";
import ordersRepository from "client-sdk/src/Repository/orders-repository";
import actionRepository from "client-sdk/src/Repository/action-repository";
import warehouseRepository from "client-sdk/src/Repository/warehouses-repository";
import partnerRepository from "client-sdk/src/Repository/partners-repository";
import { Link } from "react-router-dom";
import { mdiAssistant } from "@mdi/js";

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    paper: {},
    loading: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      height: "50vh",
      alignSelf: "center",
      justifyContent: "center",
      background: "#ccc",
    },
    searchBox: {
      display: "flex",
      position: "relative",
      backgroundColor: theme.palette.common.white,
      borderRadius: "4px",
      padding: "10px 0",
      boxShadow: "0px 2px 4px 1px rgba(0,0,0,0.1)",
    },
    input: {
      display: "flex",
      marginLeft: theme.spacing(1),
      flex: 2,
      "@media (max-width: 768px)": {
        fontSize: "12px",
      },
    },
    iconButton: {
      padding: 8,
      marginRight: "10px",
      position: "relative",
      "&:before": {
        content: '""',
        position: "absolute",
        width: "1px",
        height: "36px",
        top: "4px",
        left: "-9px",
        backgroundColor: "#EDF1F4",
      },
    },
    textArea: {
      width: "100%",
    },
    filterBox: {
      position: "absolute",
      backgroundColor: "#fff",
      width: "100%",
      marginTop: "30px",
      top: "47px",
      right: 0,
      borderRadius: "0  0 4px  4px",
      padding: "16px 16px",
      boxSizing: "border-box",
      boxShadow: "0px 2px 4px 1px rgba(0,0,0,0.1)",
      zIndex: 10,
    },
    filterOpen: {
      display: "block",
    },
    filterClose: {
      display: "none",
    },
    paginationBox: {
      display: "flex",
    },
    selectSize: {
      width: "35px",
    },
    subtitle: {
      fontSize: "14px",
      fontWeight: 500,
      color: "#8A8A8A",
    },
    magnifyIcon: {
      transform: "rotate(-90deg)",
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: "center",
      position: "relative",
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),

      "&::before": {
        content: '""',
        position: "absolute",
        width: "100px",
        top: "50%",
        left: "50%",
        transform: "translate(-50%,-50%)",
        height: "100px",
        borderRadius: "50%",
        opacity: "0.2",
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: "16px",
      fontWeight: 500,
      textAlign: "justify",
      lineHeight: 2,
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: "center",
      position: "relative",
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      "&::before": {
        content: '""',
        position: "absolute",
        width: "100px",
        top: "50%",
        left: "50%",
        transform: "translate(-50%,-50%)",
        height: "100px",
        borderRadius: "50%",
        opacity: "0.2",
        backgroundColor: theme.palette.blue.main,
      },
    },
    btnBox: {
      display: "flex",
      flexDirection: "row-reverse",
      width: "100%",
      "@media (max-width: 768px)": {
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
      },
    },
    boxshadow: {
      position: "relative",
      backgroundColor: "white",
      padding: "20px",
      transform: "translateY(-3px)",
      boxShadow: "-6px 6px 14px 0px rgba(0,0,0,0.15) !important",
    },
    cards: {
      position: "relative",
      backgroundColor: "white",
      padding: "20px",
    },
    boxshadowSm: {
      position: "relative",
      backgroundColor: "white",
      // padding: '7px',
      transform: "translateY(-3px)",
      boxShadow: "-6px 6px 14px 0px rgba(0,0,0,0.15) !important",
    },
    cardsSm: {
      position: "relative",
      backgroundColor: "white",
      // padding: '7px',
    },
    iconHeader: {
      position: "absolute",
      top: "5px",
      right: "5px",
    },
    cardContent: {
      marginTop: "-22px",
      display: "flex",
    },
    cardContentlist: {
      justifyContent: "space-between",
      padding: " 0",
    },
    mobile: {
      maxWidth: "120%",
      gap: "16px",
      margin: "16px 0",
      "@media (max-width: 480px)": {
        marginTop: "140px",
      },
    },
  })
);

const Orders: FC<Props> = (props: Props): React.ReactElement => {
  const authContext = useContext(AuthContext);
  const { warehouseId } = authContext.user;
  const [filters, setFilters] = useState([] as Filter[]);
  const { filtersValues, setFiltersValues } = useQuery({
    page: 0,
    size: 100,
    "warehouseType.equals": "SINGLE",
    "enableGetChildren.equals": true,
  } as any);
  const [openSize, setOpenSize] = React.useState(false as boolean);
  const [openConfirmCancel, setOpenConfirmCancel] = useState(false as boolean);
  const [orderUpdate, setOrderUpdate] = useState(false as boolean);
  const [openConfirmUpdateState, setOpenConfirmUpdateState] = useState(
    false as boolean
  );

  const [id, setId] = useState(-1 as number);
  const [previewState, setPreviewState] = useState("CANCELLED" as Status);
  const [cancelDescription, setCancelDescription] = useState("" as string);
  const [ordersList, setOrdersList] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [usersLoading, setUsersLoading] = useState<boolean>(false);
  const [cardCounts, setCardCounts] = useState({});
  const [dataRefreshed, setDataRefreshed] = useState<boolean>(false);
  const [timeOutTimer] = useState<number>(5 * 60 * 1000); //5 minutes to miliseconds;

  const checkActiveCard = (name: string) => {
    const status = filtersValues["status.in"]?.split(",") || [];
    const deliveryType = filtersValues["deliveryType.equals"]?.split(",") || [];
    switch (name) {
      case "created":
        return status.includes("ORDERED");
      case "assigned":
        return status.includes("ASSIGNED");
      case "picked":
        return status.includes("PICKED");
      case "packaged":
        return status.includes("PACKAGED");
      case "supplying":
        return status.includes("SUPPLYING");
      case "vip":
        return deliveryType.includes("VIP");
      case "vvip":
        return deliveryType.includes("VVIP");
    }
  };

  const handleCardClick = (name: any) => {
    switch (name) {
      case "created":
        setFiltersValues({
          ...removeEmptyValueObject(filtersValues),
          "status.in": ["ORDERED"],

          page: 0,
          size: 100,
        });
        break;
      case "assigned":
        setFiltersValues({
          ...removeEmptyValueObject(filtersValues),
          "status.in": ["ASSIGNED"],

          page: 0,
          size: 100,
        });
        break;
      case "picked":
        setFiltersValues({
          ...removeEmptyValueObject(filtersValues),
          "status.in": ["PICKED"],
          page: 0,
          size: 100,
        });
        break;
      case "packaged":
        setFiltersValues({
          ...removeEmptyValueObject(filtersValues),
          "status.in": ["PACKAGED"],
          page: 0,
          size: 100,
        });
        break;
      case "supplying":
        setFiltersValues({
          ...removeEmptyValueObject(filtersValues),
          "status.in": ["SUPPLYING"],
          page: 0,
          size: 100,
        });
        break;
      case "vip":
        setFiltersValues({
          "status.in": [
            "SUPPLYING",
            "PACKAGED",
            "PICKED",
            "ASSIGNED",
            "ORDERED",
          ],
          ...removeEmptyValueObject(filtersValues),
          "deliveryType.equals": "VIP",
          page: 0,
          size: 100,
        });
        break;
      case "vvip":
        setFiltersValues({
          "status.in": [
            "SUPPLYING",
            "PACKAGED",
            "PICKED",
            "ASSIGNED",
            "ORDERED",
          ],
          ...removeEmptyValueObject(filtersValues),
          "deliveryType.equals": "VVIP",
          page: 0,
          size: 100,
        });
        break;
    }
  };

  const handleCardClose = () => {
    delete filtersValues["status.in"];
    setFiltersValues({
      ...removeEmptyValueObject(filtersValues),
      page: 0,
      size: 10,
    });
  };

  const handleDeliveryTypeClose = () => {
    delete filtersValues["deliveryType.equals"];
    setFiltersValues({
      ...removeEmptyValueObject(filtersValues),
      page: 0,
      size: 10,
    });
  };

  const filterTranslator = (fieldName: string) => {
    const status = {
      customerName: "نام سفارش‌دهنده:",
      "customerName.equals": "نام سفارش‌دهنده:",
      "customerName.contais": "نام سفارش‌دهنده:",
      centerName: "انبار:",
      "centerName.equals": "انبار:",
      "centerName.contains": "انبار:",
      id: "شناسه مرسوله:",
      "id.equals": "شناسه مرسوله:",
      "id.contains": "شناسه مرسوله:",
      orderId: "شناسه سفارش:",
      "orderId.equals": "شناسه سفارش:",
      "orderId.contains": "شناسه سفارش:",
      deliveryDate: "تاریخ تحویل",
      "deliveryDate.equals": "تاریخ تحویل:",
      "deliveryDate.greaterThanOrEqual": "تاریخ تحویل:",
      "deliveryDate.greaterThan": "تاریخ تحویل:",
      "deliveryDate.lessThanOrEqual": "تاریخ تحویل:",
      "deliveryDate.lessThan": "تاریخ تحویل:",
      packagedDate: "تاریخ تحویل به لجستیک:",
      "packagedDate.equals": "تاریخ تحویل به لجستیک:",
      "packagedDate.greaterThanOrEqual": "تاریخ تحویل به لجستیک:",
      "packagedDate.greaterThan": "تاریخ تحویل به لجستیک:",
      "packagedDate.lessThanOrEqual": "تاریخ تحویل به لجستیک:",
      "packagedDate.lessThan": "تاریخ تحویل به لجستیک:",
      status: "وضعیت سفارش:",
      "status.in": "وضعیت سفارش:",
      "status.notIn": "وضعیت سفارش:",
      partnerName: "شرکت:",
      "partnerName.equals": "شرکت:",
      "partnerName.contains": "شرکت:",
      weight: "وزن:",
      "weight.equals": "وزن:",
      "weight.contains": "وزن:",
      length: "ابعاد:",
      "length.equals": "ابعاد:",
      "length.contains": "ابعاد:",
    };
    return status[fieldName];
  };
  const removeFilterHandler = (fieldName: any) => {
    const filtered = { ...filtersValues };
    delete filtered[fieldName];
    setFiltersValues(filtered);
  };

  useEffect(() => {
    getPartners();
  }, []);

  useEffect(() => {
    getOrders();
    getCounts();
    setupFilters();
  }, [filtersValues]);

  useEffect(() => {
    fetchUsers();
  }, [orderUpdate]);

  useEffect(() => {
    if (dataRefreshed === false) {
      const timer2 = setTimeout(() => {
        getCounts();
        setDataRefreshed(true);
      }, timeOutTimer);
      return () => clearTimeout(timer2);
    }
  }, [dataRefreshed]);

  useEffect(() => {
    if (dataRefreshed === true) {
      const timer = setTimeout(() => {
        setDataRefreshed(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [dataRefreshed]);

  const classes = useStyles();
  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setFiltersValues({ ...filtersValues, page: page - 1 });
  };
  const getAllOrders = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const response = await ordersRepository.getAllOrdersReport({ payload });
      setAllOrders(response.data);
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };
  const getingOrders = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const { response, count } = await ordersRepository.getOrders({ payload });
      setOrdersList(response.data);
      setCount(count.data);
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };
  const getPartners = async () => {
    try {
      const { response } = await partnerRepository.getPartners({
        payload: { size: 500 },
      });
      setPartners(response.data);
      setupFilters(response.data);
    } catch (error) {
      handleError(error);
    }
  };

  const getingCounts = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const { response: created } = await ordersRepository.getOrdersCount({
        ...payload.created,
      });
      const { response: supplying } = await ordersRepository.getOrdersCount({
        ...payload.supplying,
      });
      const { response: assigned } = await ordersRepository.getOrdersCount({
        ...payload.assigned,
      });
      const { response: picked } = await ordersRepository.getOrdersCount({
        ...payload.picked,
      });
      const { response: packaged } = await ordersRepository.getOrdersCount({
        ...payload.packaged,
      });
      const { response: vip } = await ordersRepository.getOrdersCount({
        ...payload.vip,
      });
      const { response: vvip } = await ordersRepository.getOrdersCount({
        ...payload.vvip,
      });
      setCardCounts({
        created,
        assigned,
        picked,
        packaged,
        supplying,
        vip,
        vvip,
      });
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };

  const getCounts = () => {
    getingCounts({
      created: { "status.in": ["ORDERED"] },
      supplying: { "status.in": ["SUPPLYING"] },
      assigned: { "status.in": ["ASSIGNED"] },
      picked: { "status.in": ["PICKED"] },
      packaged: { "status.in": ["PACKAGED"] },
      vip: {
        "deliveryType.equals": "VIP",
        "status.in": ["SUPPLYING", "PACKAGED", "PICKED", "ASSIGNED", "ORDERED"],
      },
      vvip: {
        "deliveryType.equals": "VVIP",
        "status.in": ["SUPPLYING", "PACKAGED", "PICKED", "ASSIGNED", "ORDERED"],
      },
    });
  };

  const getOrders = () => {
    const { page, size } = filtersValues;
    getingOrders({
      ...filtersValues,
      page,
      size,
    });
  };

  const setupFilters = (partnersList: any[] = partners) => {
    Can({
      role: authContext.user.role,
      perform: "orders:filtersByCenterId",
      yes: () => {
        setFilters([
          {
            type: "input",
            id: "customerName.contains",
            label: "نام سفارش‌دهنده",
            icon: mdiAccountOutline,
          },
          {
            type: "input",
            id: "customerPhoneNumber.contains",
            label: "شماره همراه سفارش‌دهنده",
            icon: mdiPhoneOutline,
          },
          {
            type: "input",
            id: "id.equals",
            label: "شناسه سفارش",
            icon: mdiPackageVariant,
          },
          {
            type: "input",
            id: "partnerName.contains",
            label: "شرکت",
            icon: mdiHomeSearchOutline,
          },
          {
            type: "checkbox",
            id: "partnerName.notin",
            label: "شرکتها(به جز)",
            icon: mdiHomeSearchOutline,
            data: partners.map((el) => el.name),
          },
          {
            type: "input",
            id: "operatorId.equals",
            label: "اپراتور",
            icon: mdiHomeSearchOutline,
          },
          {
            type: "input",
            id: "customerProvince.contains",
            label: "استان",
            icon: mdiHomeCityOutline,
          },
          {
            type: "input",
            id: "customerCity.contains",
            label: "شهر",
            icon: mdiHomeCityOutline,
          },
          {
            type: "date-picker-time",
            id: "created.greaterThanOrEqual",
            label: "تاریخ ثبت سفارش از ",
            icon: mdiCalendar,
          },
          {
            type: "date-picker-time",
            id: "created.lessThanOrEqual",
            label: "تا تاریخ ثبت سفارش  ",
            icon: mdiCalendar,
          },
          {
            type: "date-picker",
            id: "deliveryDate.greaterThanOrEqual",
            label: "تاریخ تحویل از ",
            icon: mdiCalendar,
          },
          {
            type: "date-picker",
            id: "deliveryDate.lessThanOrEqual",
            label: "  تا تاریخ تحویل ",
            icon: mdiCalendar,
          },
          {
            type: "select",
            id: "fulfillmentLineId.equals",
            label: "مرکز پردازش",
            icon: mdiStateMachine,
            data: [1, 2],
            translateType: "fulfillmentLine",
          },
          {
            type: "checkbox",
            id: "deliveryType.in",
            label: "نوع ارسال",
            icon: mdiStateMachine,
            data: [
              "COURIER",
              "POST",
              "EXPRESS_POST",
              "SPECIAL_POST",
              "VIP",
              "VVIP",
            ],
            translateType: "SendDeliveryType",
          },
          {
            type: "checkbox",
            id: "status.in",
            label: "وضعیت",
            icon: mdiStateMachine,
            data: [
              "ORDERED",
              "DELIVERED_TO_POST",
              "DELIVERED_TO_CENTER",
              "SUPPLIED_IN_CENTER",
              "PACKAGED",
              "BUNDLED",
              "LOADED",
              "ARRIVED",
              "DELIVERED",
              "NOT_DELIVERED",
              "RETURNED",
              "SUPPLYING",
              "CANCELLED",
              "REQUESTED_TO_RESEND",
            ],
            translateType: "Order",
          },
          {
            type: "checkbox",
            id: "paymentType.in",
            label: "شیوه پرداخت",
            icon: mdiStateMachine,
            data: ["COD", "ONLINE"],
            translateType: "COD",
          },
          {
            type: "checkbox",
            id: "deliveryLocationType.in",
            label: "نوع تحویل",
            icon: mdiStateMachine,
            data: ["IN_RONAD", "IN_CUSTOMER_ADDRESS"],
            translateType: "order",
          },
        ]);
      },
      no: () => {
        setFilters([
          {
            type: "input",
            id: "customerName.contains",
            label: "نام سفارش‌دهنده",
            icon: mdiAccountOutline,
          },
          {
            type: "input",
            id: "customerPhoneNumber.contains",
            label: "شماره همراه سفارش‌دهنده",
            icon: mdiPhoneOutline,
          },
          {
            type: "input",
            id: "id.equals",
            label: "شناسه سفارش",
            icon: mdiPackageVariant,
          },
          {
            type: "input",
            id: "partnerName.contains",
            label: "شرکت",
            icon: mdiHomeSearchOutline,
          },
          {
            type: "checkbox",
            id: "partnerName.notIn",
            label: "شرکتها(به جز)",
            icon: mdiHomeSearchOutline,
            data: partnersList.map((el) => el.name),
            translateType: "Category",
          },
          {
            type: "input",
            id: "operatorId.equals",
            label: "اپراتور",
            icon: mdiHomeSearchOutline,
          },
          {
            type: "input",
            id: "customerProvince.contains",
            label: "استان",
            icon: mdiHomeCityOutline,
          },
          {
            type: "input",
            id: "customerCity.contains",
            label: "شهر",
            icon: mdiHomeCityOutline,
          },
          {
            type: "date-picker",
            id: "created.greaterThanOrEqual",
            label: "تاریخ ثبت سفارش از ",
            icon: mdiCalendar,
          },
          {
            type: "date-picker",
            id: "created.lessThanOrEqual",
            label: "تا تاریخ ثبت سفارش  ",
            icon: mdiCalendar,
          },
          {
            type: "select",
            id: "deliveryStartTime.equals",
            label: "بازه تحویل از",
            icon: mdiStateMachine,
            data: [
              "08:00",
              "09:00",
              "10:00",
              "11:00",
              "12:00",
              "13:00",
              "14:00",
              "15:00",
              "16:00",
              "17:00",
              "18:00",
              "19:00",
              "20:00",
              "21:00",
            ],
            translateType: "self",
          },
          {
            type: "select",
            id: "deliveryEndTime.equals",
            label: "تا بازه تحویل",
            icon: mdiStateMachine,
            data: [
              "08:00",
              "09:00",
              "10:00",
              "11:00",
              "12:00",
              "13:00",
              "14:00",
              "15:00",
              "16:00",
              "17:00",
              "18:00",
              "19:00",
              "20:00",
              "21:00",
            ],
            translateType: "self",
          },
          {
            type: "date-picker",
            id: "deliveryDate.greaterThanOrEqual",
            label: "تاریخ تحویل از ",
            icon: mdiCalendar,
          },
          {
            type: "date-picker",
            id: "deliveryDate.lessThanOrEqual",
            label: "  تا تاریخ تحویل ",
            icon: mdiCalendar,
          },
          {
            type: "select",
            id: "fulfillmentLineId.equals",
            label: "مرکز پردازش",
            icon: mdiStateMachine,
            data: [1, 2],
            translateType: "fulfillmentLine",
          },
          {
            type: "checkbox",
            id: "deliveryType.in",
            label: "نوع ارسال",
            icon: mdiStateMachine,
            data: [
              "COURIER",
              "POST",
              "EXPRESS_POST",
              "SPECIAL_POST",
              "VIP",
              "VVIP",
            ],
            translateType: "SendDeliveryType",
          },
          {
            type: "checkbox",
            id: "status.in",
            label: "وضعیت",
            icon: mdiStateMachine,
            data: [
              "ORDERED",
              "DELIVERED_TO_POST",
              "ASSIGNED",
              "PICKED",
              "PACKAGED",
              "BUNDLED",
              "LOADED",
              "ARRIVED",
              "DELIVERED",
              "NOT_DELIVERED",
              "RETURNED",
              "SUPPLYING",
              "CANCELLED",
              "REQUESTED_TO_RESEND",
            ],
            translateType: "Order",
          },
          {
            type: "checkbox",
            id: "paymentType.in",
            label: "شیوه پرداخت",
            icon: mdiStateMachine,
            data: ["COD", "ONLINE"],
            translateType: "COD",
          },
          {
            type: "checkbox",
            id: "deliveryLocationType.in",
            label: "نوع تحویل",
            icon: mdiStateMachine,
            data: ["IN_RONAD", "IN_CUSTOMER_ADDRESS"],
            translateType: "Order",
          },
          {
            type: "input",
            id: "trackingCode.contains",
            label: "کد پیگیری",
            icon: mdiAlphabeticalVariant,
          },
        ]);
      },
    });
  };
  const cancelOrder = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const pagination = await ordersRepository.cancelOrder({ payload });
      handleNotfication("سفارش مورد نظر شمابا موفقیت لغو شد", "success");
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };
  const handleCancelOrder = async () => {
    await cancelOrder({
      id,
      body: { description: cancelDescription },
    });
    getOrders();
    getCounts();
    setOpenConfirmCancel(false);
  };
  const updateOrderStatus = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const { pagination, callback } = await ordersRepository.updateOrderStatus(
        {
          payload,
        }
      );
      callback && callback();
      handleNotfication("تغییر وضعیت سفارش با موفقیت انجام شد", "success");
    } catch (error) {
      setError(error.message);
      handleNotfication("تغییر وضعیت سفارش انجام نشد", "error");
    } finally {
      setLoading(false);
    }
  };
  const changeStatusOrder = () => {
    const callback = () => {
      getOrders();
      getCounts();
      setupFilters();
    };
    updateOrderStatus({
      id,
      callback,
      body: { status: previewState },
      pagination: filtersValues,
    });
    setOpenConfirmUpdateState(false);
  };
  const updateAllOrderAssign = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const { description, pagination, callback } =
        await ordersRepository.updateAllOrderAssign({ payload });
      callback && callback();
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };
  const actionRetrieveRubikaOrders = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const response = await actionRepository.actionRetrieveRubikaOrders({
        payload,
      });
      response.data.length === 0
        ? handleNotfication("سفارش جدیدی اضافه نشده است", "warn")
        : handleNotfication("سفارش جدیدی اضافه شد", "success");
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };
  const changeStatusOrderWhitId = (data: any) => {
    const { ids, operatorId, description, aggregate } = data;
    const callback = () => {
      getOrders();
      getCounts();
      setupFilters();
    };
    updateAllOrderAssign({
      ids,
      callback,
      operatorId,
      description,
      pagination: filtersValues,
    });
    setOpenConfirmUpdateState(false);
    setOrderUpdate(!orderUpdate);
    if (aggregate) {
      props.history.push(`/orders/receipt/bulk?ids=${ids.join()}`);
    }
  };

  const handleOpenConfirmCancele = (id: number) => {
    setOpenConfirmCancel(true);
    setId(id);
  };
  const handleOpenConfirmUpdateState = (data: { id: number; body: Status }) => {
    setOpenConfirmUpdateState(true);
    setId(data.id);
    setPreviewState(data.body);
  };

  const handleClose = () => {
    setOpenConfirmCancel(false);
    setOpenConfirmUpdateState(false);
    setOpenSize(false);
  };
  const convertArrayItemsToString = () => {
    const array = allOrders;
    const reducer = (total: any, currentValue: any) => total + currentValue;
    const newArray = array?.map((obj) => {
      return {
        ...obj,
        items: obj.items.reduce(reducer).productName,
      };
    });
    return newArray;
  };

  const findOrderStatus = (id: number) => {
    const status = ordersList.find((order) => order.id === id)?.status;
    return status && translate(status, "order");
  };

  const handleChangeSizePage = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setFiltersValues({ ...filtersValues, page: 0, size: event.target.value });
  };
  const getUsers = async (actions: any) => {
    const payload = { ...actions };
    try {
      setUsersLoading(true);
      const response = await warehouseRepository.getWarehouseOperatorsById({
        payload,
      });
      setUsers(response.data.content);
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setUsersLoading(false);
    }
  };
  const fetchUsers = () => {
    getUsers({
      id: { equals: warehouseId },
      role: { equals: "WAREHOUSE_OPERATOR" },
      status: { equals: "ACTIVE" },
      with: "orders-count",
      page: 0,
      size: 100,
    });
  };

  return (
    <AuthConsumer>
      {({ user }) => (
        <div className={classes.root}>
          <Grid container>
            <Grid container item xs={12} sm={6} alignItems="center">
              <Typography
                variant="h4"
                component="div"
                style={{ fontSize: "18px" }}
              >
                {"لیست سفارش‌ها"}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <RenderFilter
                filters={filters}
                onFilter={(filtervalues: any) => {
                  setFiltersValues({
                    ...removeEmptyValueObject(filtervalues),
                    page: 0,
                    size: 100,
                    "enableGetChildren.equals": true,
                  });
                }}
                mainFiltersCount={0}
                baseFilter={{
                  type: "input",
                  id: "id.equals",
                  placeholder: "شناسه سفارش",
                }}
                filterValues={filtersValues}
              />
            </Grid>
            <Grid container lg={12} xs={12} className={classes.mobile}>
              <Grid item md={6} lg xs={12}>
                <Card
                  className={
                    checkActiveCard("vip")
                      ? classes.boxshadowSm
                      : classes.cardsSm
                  }
                  style={{ marginBottom: "10px" }}
                >
                  <CardHeader
                    action={
                      checkActiveCard("vip") && (
                        <IconButton
                          aria-label="close"
                          onClick={handleDeliveryTypeClose}
                          className={classes.iconHeader}
                          data-cy="cardCloseBtn"
                        >
                          <Icon
                            path={mdiClose}
                            size={1}
                            style={{
                              color: "#Ffffff",
                              backgroundColor: "#FFA65A",
                              borderRadius: "3px",
                            }}
                          />
                        </IconButton>
                      )
                    }
                  />
                  <CardContent className={classes.cardContent}>
                    <IconButton
                      type="submit"
                      onClick={(name) => handleCardClick("vip")}
                      aria-label="search"
                      style={{
                        backgroundColor: "#fce8ff",
                        height: "60px",
                        width: "60px",
                        borderRadius: "4px",
                        margin: "-20px 0",
                      }}
                      data-cy="progressCardBtn"
                    >
                      <VipIcon />
                    </IconButton>
                    <Grid
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        marginRight: "10px",
                      }}
                    >
                      <Typography
                        style={{ fontWeight: 600, marginTop: "-20px" }}
                        data-cy="processingCount"
                      >
                        {cardCounts?.vip?.data}
                      </Typography>
                      <Typography
                        style={{
                          fontSize: "12px",
                          marginBottom: "-20px",
                          fontWeight: 550,
                          color: "#666666",
                        }}
                      >
                        ارسال ویژه
                      </Typography>
                    </Grid>
                  </CardContent>
                </Card>
                <Card
                  className={
                    checkActiveCard("vvip")
                      ? classes.boxshadowSm
                      : classes.cardsSm
                  }
                >
                  <CardHeader
                    action={
                      checkActiveCard("vvip") && (
                        <IconButton
                          aria-label="close"
                          onClick={handleDeliveryTypeClose}
                          className={classes.iconHeader}
                          data-cy="cardCloseBtn"
                        >
                          <Icon
                            path={mdiClose}
                            size={1}
                            style={{
                              color: "#Ffffff",
                              backgroundColor: "#FFA65A",
                              borderRadius: "3px",
                            }}
                          />
                        </IconButton>
                      )
                    }
                  />
                  <CardContent className={classes.cardContent}>
                    <IconButton
                      type="submit"
                      onClick={(name) => handleCardClick("vvip")}
                      aria-label="search"
                      style={{
                        backgroundColor: "#e6efff",
                        height: "60px",
                        width: "60px",
                        borderRadius: "4px",
                        margin: "-20px 0",
                      }}
                      data-cy="progressCardBtn"
                    >
                      <VvipIcon />
                    </IconButton>
                    <Grid
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        marginRight: "10px",
                      }}
                    >
                      <Typography
                        style={{ fontWeight: 600, marginTop: "-20px" }}
                        data-cy="processingCount"
                      >
                        {cardCounts?.vvip?.data}
                      </Typography>
                      <Typography
                        style={{
                          fontSize: "12px",
                          marginBottom: "-20px",
                          fontWeight: 550,
                          color: "#666666",
                        }}
                      >
                        ارسال فوری
                      </Typography>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item md={6} lg xs={12}>
                <Card
                  className={
                    checkActiveCard("supplying")
                      ? classes.boxshadow
                      : classes.cards
                  }
                >
                  <CardHeader
                    action={
                      checkActiveCard("supplying") && (
                        <IconButton
                          aria-label="close"
                          onClick={handleCardClose}
                          className={classes.iconHeader}
                          data-cy="cardCloseBtn"
                        >
                          <Icon
                            path={mdiClose}
                            size={1}
                            style={{
                              color: "#Ffffff",
                              backgroundColor: "#FFA65A",
                              borderRadius: "3px",
                            }}
                          />
                        </IconButton>
                      )
                    }
                  />
                  <CardContent className={classes.cardContent}>
                    <IconButton
                      type="submit"
                      onClick={(name) => handleCardClick("supplying")}
                      aria-label="search"
                      style={{
                        backgroundColor: "#fefe8e",
                        height: "100px",
                        width: "100px",
                        borderRadius: "4px",
                        margin: "-20px 0",
                      }}
                      data-cy="progressCardBtn"
                    >
                      <SupplyingIcon />
                    </IconButton>
                    <Grid
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        marginRight: "10px",
                      }}
                    >
                      <Typography
                        variant="h6"
                        component="h6"
                        style={{ fontWeight: 600, marginTop: "-20px" }}
                        data-cy="processingCount"
                      >
                        {cardCounts?.supplying?.data}
                      </Typography>
                      <Typography
                        style={{
                          fontSize: "16px",
                          marginBottom: "-20px",
                          fontWeight: 550,
                          color: "#666666",
                        }}
                      >
                        در انتظار تامین موجودی
                      </Typography>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item md={6} lg xs={12}>
                <Card
                  className={
                    checkActiveCard("created")
                      ? classes.boxshadow
                      : classes.cards
                  }
                >
                  <CardHeader
                    action={
                      checkActiveCard("created") && (
                        <IconButton
                          aria-label="close"
                          onClick={handleCardClose}
                          className={classes.iconHeader}
                          data-cy="cardCloseBtn"
                        >
                          <Icon
                            path={mdiClose}
                            size={1}
                            style={{
                              color: "#Ffffff",
                              backgroundColor: "#FFA65A",
                              borderRadius: "3px",
                            }}
                          />
                        </IconButton>
                      )
                    }
                  />
                  <CardContent className={classes.cardContent}>
                    <IconButton
                      type="submit"
                      onClick={(name) => handleCardClick("created")}
                      aria-label="search"
                      style={{
                        backgroundColor: "#CCEEEE",
                        height: "100px",
                        width: "100px",
                        borderRadius: "4px",
                        margin: "-20px 0",
                      }}
                      data-cy="progressCardBtn"
                    >
                      <CreatedIcon />
                    </IconButton>
                    <Grid
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        marginRight: "10px",
                      }}
                    >
                      <Typography
                        variant="h6"
                        component="h6"
                        style={{ fontWeight: 600, marginTop: "-20px" }}
                        data-cy="processingCount"
                      >
                        {cardCounts?.created?.data}
                      </Typography>
                      <Typography
                        style={{
                          fontSize: "16px",
                          marginBottom: "-20px",
                          fontWeight: 550,
                          color: "#666666",
                        }}
                      >
                        ایجاد شده
                      </Typography>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item md={6} lg xs={12}>
                <Card
                  className={
                    checkActiveCard("assigned")
                      ? classes.boxshadow
                      : classes.cards
                  }
                >
                  <CardHeader
                    action={
                      checkActiveCard("assigned") && (
                        <IconButton
                          aria-label="close"
                          onClick={handleCardClose}
                          className={classes.iconHeader}
                          data-cy="cardCloseBtn"
                        >
                          <Icon
                            path={mdiClose}
                            size={1}
                            style={{
                              color: "#Ffffff",
                              backgroundColor: "#FFA65A",
                              borderRadius: "3px",
                            }}
                          />
                        </IconButton>
                      )
                    }
                  />
                  <CardContent className={classes.cardContent}>
                    <IconButton
                      type="submit"
                      aria-label="search"
                      onClick={(name) => handleCardClick("assigned")}
                      style={{
                        backgroundColor: "#FCEFDB",
                        height: "100px",
                        width: "100px",
                        borderRadius: "4px",
                        margin: "-20px 0",
                      }}
                      data-cy="courierCardBtn"
                    >
                      <AssignedIcon />
                    </IconButton>
                    <Grid
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        marginRight: "10px",
                      }}
                    >
                      <Typography
                        variant="h6"
                        component="h6"
                        style={{ fontWeight: 600, marginTop: "-20px" }}
                        data-cy="deliveringCount"
                      >
                        {cardCounts?.assigned?.data}
                      </Typography>
                      <Typography
                        style={{
                          fontSize: "16px",
                          marginBottom: "-20px",
                          fontWeight: 550,
                          color: "#666666",
                        }}
                      >
                        اختصاص داده شده
                      </Typography>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item md={6} lg xs={12}>
                <Card
                  className={
                    checkActiveCard("picked")
                      ? classes.boxshadow
                      : classes.cards
                  }
                >
                  <CardHeader
                    action={
                      checkActiveCard("picked") && (
                        <IconButton
                          aria-label="close"
                          onClick={handleCardClose}
                          className={classes.iconHeader}
                          data-cy="cardCloseBtn"
                        >
                          <Icon
                            path={mdiClose}
                            size={1}
                            style={{
                              color: "#Ffffff",
                              backgroundColor: "#FFA65A",
                              borderRadius: "3px",
                            }}
                          />
                        </IconButton>
                      )
                    }
                  />

                  <CardContent className={classes.cardContent}>
                    <IconButton
                      type="submit"
                      aria-label="search"
                      onClick={(name) => handleCardClick("picked")}
                      style={{
                        backgroundColor: "#E3DAF7",
                        height: "100px",
                        width: "100px",
                        borderRadius: "4px",
                        margin: "-20px 0",
                      }}
                      data-cy="postCardBtn"
                    >
                      <PickIcon />
                    </IconButton>
                    <Grid
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        marginRight: "10px",
                      }}
                    >
                      <Typography
                        variant="h6"
                        component="h6"
                        style={{ fontWeight: 600, marginTop: "-20px" }}
                        data-cy="deliverdPostCount"
                      >
                        {cardCounts?.picked?.data}
                      </Typography>
                      <Typography
                        style={{
                          fontSize: "16px",
                          marginBottom: "-20px",
                          fontWeight: 550,
                          color: "#666666",
                        }}
                      >
                        برداشته شده
                      </Typography>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item md={6} lg xs={12}>
                <Card
                  className={
                    checkActiveCard("packaged")
                      ? classes.boxshadow
                      : classes.cards
                  }
                >
                  <CardHeader
                    action={
                      checkActiveCard("packaged") && (
                        <IconButton
                          aria-label="close"
                          onClick={handleCardClose}
                          className={classes.iconHeader}
                          data-cy="cardCloseBtn"
                        >
                          <Icon
                            path={mdiClose}
                            size={1}
                            style={{
                              color: "#Ffffff",
                              backgroundColor: "#FFA65A",
                              borderRadius: "3px",
                            }}
                          />
                        </IconButton>
                      )
                    }
                  />
                  <CardContent className={classes.cardContent}>
                    <IconButton
                      type="submit"
                      aria-label="search"
                      onClick={(name) => handleCardClick("packaged")}
                      style={{
                        backgroundColor: "#D6EBD3",
                        height: "100px",
                        width: "100px",
                        borderRadius: "4px",
                        margin: "-20px 0",
                      }}
                      data-cy="deliveredCardBtn"
                    >
                      <PackIcon />
                    </IconButton>
                    <Grid
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        marginRight: "10px",
                      }}
                    >
                      <Typography
                        variant="h6"
                        component="h6"
                        style={{ fontWeight: 600, marginTop: "-20px" }}
                        data-cy="deliveredCount"
                      >
                        {cardCounts?.packaged?.data}
                      </Typography>
                      <Typography
                        style={{
                          fontSize: "16px",
                          marginBottom: "-20px",
                          fontWeight: 550,
                          color: "#666666",
                        }}
                      >
                        بسته بندی شده
                      </Typography>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            <Grid xs={12} sm={6} container alignItems="center">
              {loading ? (
                ""
              ) : (
                <>
                  {ordersList.length > 0 && (
                    <>
                      <div className={classes.subtitle}>
                        {"تعداد کل سفارش‌ها:"}
                      </div>
                      <div>
                        &nbsp;
                        <b>{`${count}`}</b>
                      </div>
                    </>
                  )}
                </>
              )}
            </Grid>
            <Grid
              container
              xs={12}
              md={6}
              direction="row-reverse"
              alignItems="center"
            >
              <div className={classes.btnBox}>
                <Can
                  role={user.role}
                  perform="orders:btnGetNewOrders"
                  yes={() => (
                    <>
                      <Button
                        variant="contained"
                        color="secondary"
                        size={
                          checkMatchMedia("(max-width: 1024px)")
                            ? "small"
                            : "large"
                        }
                        onClick={() => actionRetrieveRubikaOrders({})}
                      >
                        {"دریافت سفارش جدید"}
                      </Button>
                    </>
                  )}
                />

                <Can
                  role={user.role}
                  perform="orders:downloadReportCSV"
                  yes={() => (
                    <>
                      <CSV
                        ordersDispatch={getAllOrders}
                        data={allOrders}
                        filtersValues={filtersValues}
                        loading={loading}
                      />
                      <ExelExport
                        data={convertArrayItemsToString()}
                        loading={props.loading}
                        filtersValues={filtersValues}
                        handleRequest={getAllOrders}
                      />
                      <Link to="/operator-assignment">
                        <Button
                          style={{
                            marginLeft: "5px",
                            backgroundColor: "#EA6640",
                          }}
                          variant="contained"
                          color="primary"
                          startIcon={<Icon path={mdiAssistant} size={1} />}
                        >
                          اختصاص اتوماتیک
                        </Button>
                      </Link>
                    </>
                  )}
                />
              </div>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12} style={{ margin: "16px 0" }}>
              {loading ? (
                <div className={classes.loading}>
                  <CircularProgress />
                </div>
              ) : (
                <>
                  {ordersList.length > 0 ? (
                    <List
                      data={ordersList}
                      users={users}
                      loading={usersLoading}
                      handleConfirmCanceleBox={handleOpenConfirmCancele}
                      changeStatusOrderWhitId={changeStatusOrderWhitId}
                      handleOpenConfirmUpdateState={
                        handleOpenConfirmUpdateState
                      }
                      filterVal={filtersValues}
                      onFilterValue={(filtervalues: any) => {
                        setFiltersValues({
                          ...removeEmptyValueObject(filtervalues),
                          ...filtersValues,
                          page: 0,
                          size: 100,
                        });
                      }}
                      removeFilterHandler={removeFilterHandler}
                      filterLabelTranslator={filterTranslator}
                    />
                  ) : (
                    <Typography variant="h3" component="div">
                      سفارشی موجود نیست
                    </Typography>
                  )}
                </>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {ordersList.length > 0 && (
                <FormControl
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      marginLeft: "10px",
                      color: "#8a8a8a",
                    }}
                  >
                    {" "}
                    تعداد نمایش در هر صفحه:
                  </div>
                  <Select
                    open={openSize}
                    onClose={handleClose}
                    onOpen={() => setOpenSize(true)}
                    value={filtersValues.size}
                    onChange={handleChangeSizePage}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid xs={12} sm={6} container item direction="row-reverse">
              {ordersList.length > 0 && (
                <Pagination
                  count={Math.ceil(count / filtersValues.size)}
                  color="secondary"
                  page={filtersValues.page + 1}
                  onChange={handleChangePage}
                />
              )}
            </Grid>
          </Grid>

          <Dialog
            open={openConfirmCancel}
            fullWidth
            onClose={handleClose}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogContent>
              <div
                style={{
                  position: "absolute",
                  left: "17px",
                  zIndex: 10,
                }}
              >
                {handleClose ? (
                  <IconButton
                    aria-label="close"
                    // className={classes.closeButton}
                    onClick={handleClose}
                  >
                    <Icon path={mdiClose} size={1} />
                  </IconButton>
                ) : null}
              </div>
              <div className={classes.cancelIcon}>
                <Icon
                  path={mdiClose}
                  size={4}
                  color={theme.palette.error.main}
                />
              </div>
              <div
                className={classes.cancelTitle}
              >{`از لغو سفارش با شناسه ${id} مطمئن هستید؟`}</div>
              <TextField
                className={classes.textArea}
                placeholder={"علت لغو..."}
                value={cancelDescription}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCancelDescription(e.target.value)
                }
              />
            </DialogContent>
            <DialogActions className={classes.actionsBox}>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Button
                    fullWidth
                    autoFocus
                    onClick={handleClose}
                    variant="contained"
                    color="secondary"
                    size="large"
                  >
                    {"خیر"}
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleCancelOrder}
                    disabled={!(cancelDescription.length > 0)}
                    size="large"
                  >
                    {"بله"}
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </Dialog>

          <Dialog
            fullWidth
            open={openConfirmUpdateState}
            onClose={handleClose}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogContent>
              <div
                style={{
                  position: "absolute",
                  left: "17px",
                  zIndex: 10,
                }}
              >
                {handleClose ? (
                  <IconButton
                    aria-label="close"
                    // className={classes.closeButton}
                    onClick={handleClose}
                  >
                    <Icon path={mdiClose} size={1} />
                  </IconButton>
                ) : null}
              </div>
              <div className={classes.editIcon}>
                <Icon
                  path={mdiPencilOutline}
                  size={4}
                  color={theme.palette.blue.main}
                />
              </div>
              <div className={classes.cancelTitle}>
                {`از تغییر تگ وضعیت سفارش
                با شناسه  ${id} از
 "${id > -1 && findOrderStatus(id)}"
 به
" ${translate(previewState.replace(/"/g, "") as Status, "order")}"

 
 
 مطمئن هستید؟`}
              </div>
            </DialogContent>
            <Divider />
            <DialogActions className={classes.actionsBox}>
              <Grid container spacing={2} justifyContent="flex-end">
                <Grid item xs={2}>
                  <Button
                    fullWidth
                    autoFocus
                    onClick={handleClose}
                    variant="outlined"
                    size="large"
                  >
                    {"انصراف"}
                  </Button>
                </Grid>
                <Grid item xs={5}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={changeStatusOrder}
                    size="large"
                    color="primary"
                  >
                    {"تغییر وضعیت سفارش"}
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </AuthConsumer>
  );
};

export default Orders;
